import { useEffect, useRef, useState } from 'react'
import { CreditCard, Building2, Upload, CheckCircle2, X } from 'lucide-react'
import { Input } from '@/components/ui'
import { Switch } from '@/components/ui'
import { Textarea } from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import type { PaymentConfig, BankTransferConfig } from '@/stores'

interface PaymentsConfigSectionProps {
  value: PaymentConfig | null
  onChange: (value: PaymentConfig | null) => void
}

interface BusinessBankDefaults {
  bank_beneficiary_name?: string | null
  bank_name?: string | null
  bank_account_or_clabe?: string | null
  bank_instructions?: string | null
  bank_details_image?: string | null
}

const defaultBankTransfer: BankTransferConfig = {
  enabled: true,
  currency: 'MXN',
  amountToleranceCents: 50,
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </label>
  )
}

export function PaymentsConfigSection({ value, onChange }: PaymentsConfigSectionProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [bizDefaults, setBizDefaults] = useState<BusinessBankDefaults | null>(null)

  useEffect(() => {
    // Business-default hints (CLABE parameterization spec §7). 403 /
    // failure (viewer lacks business:read) degrades silently to no hints.
    apiClient
      .get('/api/commerce/business')
      .then((d) => setBizDefaults(d))
      .catch(() => setBizDefaults(null))
  }, [])

  const defaultHint = (personaValue: string | undefined, defaultValue: string | null | undefined) =>
    !personaValue && defaultValue ? (
      <p className="text-xs text-muted-foreground italic">
        Using business default: {defaultValue}
      </p>
    ) : null

  const handleSectionToggle = (enabled: boolean) => {
    if (!enabled) {
      onChange(null)
    } else {
      onChange({
        bankTransfer: { ...defaultBankTransfer },
        card: { enabled: false },
      })
    }
  }

  const handleBankTransferToggle = (enabled: boolean) => {
    onChange({
      ...value,
      bankTransfer: enabled
        ? { ...(value?.bankTransfer ?? defaultBankTransfer), enabled: true }
        : undefined,
    })
  }

  const handleCardToggle = (enabled: boolean) => {
    onChange({
      ...value,
      card: { enabled },
    })
  }

  const updateBankTransfer = (patch: Partial<BankTransferConfig>) => {
    onChange({
      ...value,
      bankTransfer: {
        ...(value?.bankTransfer ?? defaultBankTransfer),
        ...patch,
      },
    })
  }

  const handleImageUpload = async (file: File) => {
    setIsUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await apiClient.fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }
      const data = await response.json()
      const mediaRef: string = data.media_ref ?? data.id ?? data.ref
      updateBankTransfer({ detailsImage: mediaRef })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      void handleImageUpload(file)
    }
    // Reset so the same file can be re-selected after clear
    e.target.value = ''
  }

  const sectionEnabled = value !== null
  const bt = value?.bankTransfer
  const cardEnabled = value?.card?.enabled ?? false

  return (
    <div className="space-y-4">
      {/* Section header + master toggle */}
      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary" />
          <span className="font-medium">Payments</span>
        </div>
        <Toggle
          checked={sectionEnabled}
          onChange={handleSectionToggle}
          label={sectionEnabled ? 'Enabled' : 'Disabled'}
        />
      </div>

      {!sectionEnabled && (
        <p className="text-sm text-muted-foreground px-1">
          Enable to configure payment methods for this persona.
        </p>
      )}

      {sectionEnabled && (
        <div className="space-y-6">
          {/* ── Bank Transfer ── */}
          <div className="space-y-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Payment-method identity accent — rule-5 leave (A7 Google-"G" precedent) */}
                <Building2 className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-sm">Bank Transfer / CLABE</span>
              </div>
              <Toggle
                checked={bt?.enabled ?? false}
                onChange={handleBankTransferToggle}
                label={bt?.enabled ? 'Enabled' : 'Disabled'}
              />
            </div>

            {!bt && (
              <p className="text-xs text-muted-foreground">
                Enable to send bank details over WhatsApp and verify payment screenshots.
              </p>
            )}

            {bt && (
              <div className="space-y-4">
                {/* Beneficiary + Bank */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">
                      Beneficiary Name
                    </label>
                    <Input
                      value={bt.beneficiaryName ?? ''}
                      onChange={(e) => updateBankTransfer({ beneficiaryName: e.target.value || undefined })}
                      placeholder="e.g. Jaso Bakery S.A. de C.V."
                    />
                    {/* Atomic trio: resolve_bank_details() anchors beneficiary/bank/CLABE
                        wholesale on the persona CLABE. If accountOrClabe is set, the
                        profile beneficiary would NEVER actually serve, so the hint must
                        stay silent even though this field itself is empty. */}
                    {!bt.accountOrClabe && defaultHint(bt.beneficiaryName, bizDefaults?.bank_beneficiary_name)}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">
                      Bank Name
                    </label>
                    <Input
                      value={bt.bankName ?? ''}
                      onChange={(e) => updateBankTransfer({ bankName: e.target.value || undefined })}
                      placeholder="e.g. BBVA"
                    />
                    {/* Same atomic-trio reasoning as the beneficiary hint above. */}
                    {!bt.accountOrClabe && defaultHint(bt.bankName, bizDefaults?.bank_name)}
                  </div>
                </div>

                {/* Account / CLABE */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">
                    Account / CLABE *
                  </label>
                  <Input
                    value={bt.accountOrClabe ?? ''}
                    onChange={(e) => updateBankTransfer({ accountOrClabe: e.target.value || undefined })}
                    placeholder="18-digit CLABE or account number"
                    data-testid="account-or-clabe"
                  />
                  {defaultHint(bt.accountOrClabe, bizDefaults?.bank_account_or_clabe)}
                  <p className="text-xs text-muted-foreground">
                    Sent to the customer when they choose bank transfer at checkout.
                  </p>
                </div>

                {/* Currency + Amount Tolerance */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">
                      Currency
                    </label>
                    <Input
                      value={bt.currency}
                      onChange={(e) => updateBankTransfer({ currency: e.target.value || 'MXN' })}
                      placeholder="MXN"
                      maxLength={3}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">
                      Amount Tolerance (cents)
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={bt.amountToleranceCents}
                      onChange={(e) =>
                        updateBankTransfer({
                          amountToleranceCents: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      How many cents off the exact amount is still accepted (e.g. 50 = ±$0.50).
                    </p>
                  </div>
                </div>

                {/* Instructions */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">
                    Payment Instructions
                  </label>
                  <Textarea
                    value={bt.instructions ?? ''}
                    onChange={(e) => updateBankTransfer({ instructions: e.target.value || undefined })}
                    placeholder="e.g. Transfer the exact amount and send a screenshot of the receipt."
                    rows={3}
                  />
                  {defaultHint(bt.instructions, bizDefaults?.bank_instructions)}
                </div>

                {/* Vision Model */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">
                    Vision Model (override)
                  </label>
                  <Input
                    value={bt.visionModel ?? ''}
                    onChange={(e) => updateBankTransfer({ visionModel: e.target.value || undefined })}
                    placeholder="Leave blank to derive from persona's base model"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used only while no Payment-proof check model is saved on AI models. Defaults to the
                    persona's base model with the <code>-rag</code> suffix stripped (e.g.{' '}
                    <code>gpt-5.2-rag</code> → <code>gpt-5.2</code>).
                  </p>
                </div>

                {/* Business card image upload */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">
                    Bank Details Image (optional)
                  </label>
                  {bt.detailsImage ? (
                    <div className="flex items-center gap-3 p-3 bg-success/10 border border-success/20 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-success">
                          Image set
                        </p>
                        <p className="text-xs text-success truncate font-mono">
                          {bt.detailsImage}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          updateBankTransfer({ detailsImage: undefined })
                          if (fileInputRef.current) fileInputRef.current.value = ''
                        }}
                        className="p-1 hover:bg-success/20 rounded-sm shrink-0"
                        title="Remove image"
                        aria-label="Remove image"
                      >
                        <X className="w-4 h-4 text-success" />
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-xs text-success underline hover:no-underline shrink-0"
                        disabled={isUploading}
                      >
                        Replace
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex items-center gap-2 w-full px-3 py-2 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm text-muted-foreground">
                        {isUploading ? 'Uploading…' : 'Upload bank details card / QR code'}
                      </span>
                    </button>
                  )}

                  {!bt.detailsImage && bizDefaults?.bank_details_image && (
                    <p className="text-xs text-muted-foreground italic">
                      Using business default image
                    </p>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleFileChange}
                    data-testid="image-file-input"
                  />

                  {uploadError && (
                    <p className="text-xs text-destructive">{uploadError}</p>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Sent to the customer alongside the CLABE to show where to transfer.
                    JPEG, PNG, or PDF accepted.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Card ── */}
          <div className="space-y-3 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Payment-method identity accent — rule-5 leave (A7 Google-"G" precedent) */}
                <CreditCard className="w-4 h-4 text-purple-500" />
                <span className="font-medium text-sm">Card (Stripe)</span>
              </div>
              <Toggle
                checked={cardEnabled}
                onChange={handleCardToggle}
                label={cardEnabled ? 'Enabled' : 'Disabled'}
              />
            </div>
            {!cardEnabled && (
              <p className="text-xs text-muted-foreground">
                Enable to allow card payments via Stripe. Requires a Stripe credential set bound to
                this persona.
              </p>
            )}
            {cardEnabled && (
              <p className="text-xs text-success">
                Card payments active. Ensure a Stripe credential set is bound under Tools → Adapters.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
